import { useState } from "react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../components/ui/accordion";
import { Input } from "../components/ui/input";
import { GraduationCap, Calculator, CheckCircle2, Info, Search } from "lucide-react";

const Example = ({ children }) => (
  <div className="mt-3 rounded-lg bg-secondary px-4 py-3 text-sm font-mono num leading-relaxed">{children}</div>
);

const SECTIONS = [
  {
    id: "inclusive-exclusive",
    q: "GST-inclusive vs GST-exclusive — what's the difference?",
    text: "gst inclusive exclusive net ex gst difference price divide by 11 multiply 1.1",
    a: (
      <>
        <p><strong>GST-inclusive</strong> means the price already contains the 10% GST. It's the total a customer actually pays.
        <strong> GST-exclusive</strong> (also called "net" or "ex GST") is the price before GST is added.</p>
        <p className="mt-2">In Australia, retail prices you advertise must be GST-inclusive. In KangarooLedger you always enter the <strong>GST-inclusive total</strong> and we work out the GST for you.</p>
        <Example>
          Sale total (incl GST): $110.00<br />
          GST portion = $110 ÷ 11 = $10.00<br />
          Net (ex GST) = $110 − $10 = $100.00
        </Example>
        <p className="mt-3 text-muted-foreground">Quick rules: to find GST from an inclusive price, divide by 11. To add GST to an exclusive price, multiply by 1.1.</p>
      </>
    ),
  },
  {
    id: "standard",
    q: "Standard GST (10%)",
    text: "standard gst 10% taxable supply 1A cafe meals coffee tradie labour consulting retail",
    a: (
      <>
        <p>Most goods and services sold in Australia are "taxable supplies" and carry 10% GST. You collect this GST from customers and pay it to the ATO on your BAS (label 1A).</p>
        <p className="mt-2">Examples: cafe meals & coffee, tradie labour, consulting, most retail products.</p>
      </>
    ),
  },
  {
    id: "gst-free",
    q: "GST-free supplies",
    text: "gst free supplies basic food bread milk fruit vegetables health medical education exports credits",
    a: (
      <>
        <p>Some sales are <strong>GST-free</strong> — no GST is added, but you can still claim GST credits on related purchases. You report the sale, but the GST amount is $0.</p>
        <p className="mt-2">Common GST-free items: most basic/unprocessed food (bread, milk, fresh fruit & veg), certain health and medical services, some education, and exports.</p>
        <p className="mt-2 text-muted-foreground">Cafe tip: a takeaway loaf of bread may be GST-free, but a dine-in meal or hot coffee is taxable. When in doubt, check the ATO food list.</p>
      </>
    ),
  },
  {
    id: "input-taxed",
    q: "Input-taxed supplies",
    text: "input taxed residential rent financial supplies bank interest no credits",
    a: (
      <>
        <p>For <strong>input-taxed</strong> sales you don't charge GST <em>and</em> you generally can't claim GST credits on related purchases.</p>
        <p className="mt-2">The two main examples are residential rent and most financial supplies (e.g. bank interest).</p>
      </>
    ),
  },
  {
    id: "tax-invoice",
    q: "When do I need a tax invoice?",
    text: "tax invoice 82.50 threshold claim gst credit supplier purchase",
    a: (
      <>
        <p>To claim a GST credit on a purchase of <strong>$82.50 or more (incl GST)</strong>, you must hold a valid tax invoice from the supplier.</p>
        <p className="mt-2">KangarooLedger flags expenses over this threshold that are missing a tax invoice, so you don't over-claim on your BAS.</p>
      </>
    ),
  },
  {
    id: "bas",
    q: "What is BAS and how is GST calculated?",
    text: "bas business activity statement 1A 1B net gst payable refund calculation lodge quarterly",
    a: (
      <>
        <p>Your <strong>Business Activity Statement (BAS)</strong> reports the GST you collected on sales (1A) and the GST you paid on purchases (1B). The difference is what you owe — or get refunded.</p>
        <Example>
          GST collected on sales (1A): $2,400<br />
          GST paid on purchases (1B): $900<br />
          Net GST payable = $2,400 − $900 = $1,500
        </Example>
        <p className="mt-3">If 1B is larger than 1A, you receive a refund from the ATO instead.</p>
      </>
    ),
  },
  {
    id: "daily-takings",
    q: "Recording daily takings (cafe / retail)",
    text: "daily takings cafe retail eftpos cash card split payment method register",
    a: (
      <>
        <p>If you make lots of small sales each day, you don't need to enter every transaction. Use <strong>Add daily takings</strong> on the Income page to log one day's total, split by EFTPOS, cash and card.</p>
        <p className="mt-2">Each split is tagged with its payment method so your dashboard shows how much came in as cash vs card, and GST is still calculated on the full amount.</p>
      </>
    ),
  },
];

export default function LearnPage() {
  const [query, setQuery] = useState("");
  const ql = query.trim().toLowerCase();
  const filtered = ql
    ? SECTIONS.filter((s) => (s.q + " " + s.text).toLowerCase().includes(ql))
    : SECTIONS;

  return (
    <div className="max-w-3xl">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary grid place-items-center">
          <GraduationCap className="w-5 h-5" />
        </div>
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Learn GST & BAS</h1>
          <p className="text-muted-foreground text-sm">Plain-English guides to understanding Australian GST.</p>
        </div>
      </div>

      <div className="relative my-5">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input placeholder="Search topics — e.g. GST-free, tax invoice, BAS…" value={query}
          onChange={(e) => setQuery(e.target.value)} className="pl-9" data-testid="learn-search" />
      </div>

      {!ql && (
        <div className="grid sm:grid-cols-3 gap-3 mb-6">
          {[
            { icon: Calculator, t: "Divide by 11", d: "GST from an inclusive price" },
            { icon: CheckCircle2, t: "$82.50", d: "Tax-invoice threshold" },
            { icon: Info, t: "1A − 1B", d: "Net GST on your BAS" },
          ].map((c) => (
            <div key={c.t} className="rounded-xl border border-border bg-card p-4">
              <c.icon className="w-5 h-5 text-primary mb-2" />
              <div className="num font-bold text-lg">{c.t}</div>
              <div className="text-xs text-muted-foreground">{c.d}</div>
            </div>
          ))}
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-card px-5 py-10 text-center text-sm text-muted-foreground" data-testid="learn-no-results">
          No topics match “{query}”. Try “GST-free”, “invoice” or “BAS”.
        </div>
      ) : (
        <div className="rounded-2xl border border-border bg-card px-5 py-2" data-testid="learn-accordion">
          <Accordion type="single" collapsible defaultValue={ql ? filtered[0].id : "inclusive-exclusive"}>
            {filtered.map((s) => (
              <AccordionItem key={s.id} value={s.id} data-testid={`learn-item-${s.id}`}>
                <AccordionTrigger className="text-left font-medium">{s.q}</AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-foreground/80">{s.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      )}

      <p className="text-xs text-muted-foreground mt-4">
        General guidance only — not tax advice. Check ato.gov.au or your registered tax agent for your specific situation.
      </p>
    </div>
  );
}
